import { useEffect, useRef } from 'react'
import { useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import * as esri from 'esri-leaflet'

interface MapEventHandlerProps {
  onReachIdFound: (reachId: string) => void
  setIsSearchingReachId: (loading: boolean) => void
  setCanFetchForecast: (canFetch: boolean) => void
  isActive: boolean
  selectedReachId: string | null
}

/**
 * Component to handle map click events for reach ID identification
 * Uses ArcGIS identify service to find stream identifiers
 */
export function MapEventHandler({
  onReachIdFound,
  setIsSearchingReachId,
  setCanFetchForecast,
  isActive,
  selectedReachId
}: MapEventHandlerProps) {
  const map = useMap()
  const markerRef = useRef<L.Marker | null>(null)

  // Remove marker when selectedReachId is cleared
  useEffect(() => {
    if (!selectedReachId && markerRef.current) {
      map.removeLayer(markerRef.current)
      markerRef.current = null
    }
  }, [selectedReachId, map])

  useMapEvents({
    click: (e) => {
      // Only process clicks if reach-id mode is active
      if (!isActive) return
      
      console.log('Map clicked at:', e.latlng)

      // Check if click is within India bounds before processing
      const indiaClickBounds = L.latLngBounds(
        L.latLng(6, 68),    // Southwest of India
        L.latLng(38, 98)    // Northeast of India  
      )

      if (!indiaClickBounds.contains(e.latlng)) {
        console.log('Click outside India bounds, ignoring ArcGIS request')
        return
      }

      // Zoom behavior - fly to location if zoom is low, otherwise just center
      if (map.getZoom() <= 9.5) {
        map.flyTo(e.latlng, 10)
        return
      } else {
        map.flyTo(e.latlng)
      }

      // Remove existing marker if it exists
      if (markerRef.current) {
        map.removeLayer(markerRef.current)
      }

      // Add new marker at clicked location
      markerRef.current = L.marker(e.latlng).addTo(map)

      // Only perform ArcGIS identify features request if zoomed in enough
      if (map.getZoom() >= 10) {
        // Start loading indicator
        setIsSearchingReachId(true)
        setCanFetchForecast(false)

        esri
          .identifyFeatures({
            url: "https://livefeeds2.arcgis.com/arcgis/rest/services/GEOGLOWS/GlobalWaterModel_Medium/MapServer"
          })
          .on(map)
          .at([e.latlng.lat, e.latlng.lng])
          .tolerance(10) // map pixels to buffer search point
          .precision(3) // decimals in the returned coordinate pairs
          .run(function (error, featureCollection) {
            // Always stop loading indicator
            setIsSearchingReachId(false)

            if (error) {
              console.error("Error finding the reach_id:", error)
              if (markerRef.current) {
                map.removeLayer(markerRef.current)
                markerRef.current = null
              }
              return
            }

            console.log("Feature collection:", featureCollection)

            if (featureCollection?.features?.length > 0) {
              const feature = featureCollection.features[0]
              const reachId = feature.properties["COMID (Stream Identifier)"]
              console.log("COMID:", reachId)

              if (reachId) {
                onReachIdFound(reachId.toString())
                setCanFetchForecast(true)
              } else {
                console.log("No reachId found in feature, removing marker")
                if (markerRef.current) {
                  map.removeLayer(markerRef.current)
                  markerRef.current = null
                }
              }
            } else {
              console.log("No features found, removing marker")
              if (markerRef.current) {
                map.removeLayer(markerRef.current)
                markerRef.current = null
              }
            }
          })
      }
    },
    zoomend: (e: any) => {
      console.log('Zoom level:', e.target.getZoom())
    }
  })

  return null
}
